import { create } from "zustand";
import { persist } from "zustand/middleware";

export type CartLine = {
  productId: number;
  name: string;
  unitPrice: string;
  quantity: number;
};

type CartState = {
  items: Record<number, CartLine>;
  // zustand's persist middleware reads localStorage asynchronously, so on a
  // fresh page load `items` starts empty for a moment before the saved cart
  // loads. Callers that redirect on an "empty cart" (checkout) must wait for
  // this to flip true first, or they'll bounce a customer with a real cart.
  hasHydrated: boolean;
  setQuantity: (product: { id: number; name: string; price: string }, quantity: number) => void;
  clear: () => void;
};

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: {},
      hasHydrated: false,
      setQuantity: (product, quantity) =>
        set((state) => {
          const items = { ...state.items };
          if (quantity <= 0) {
            delete items[product.id];
          } else {
            items[product.id] = {
              productId: product.id,
              name: product.name,
              unitPrice: product.price,
              quantity,
            };
          }
          return { items };
        }),
      clear: () => set({ items: {} }),
    }),
    {
      name: "ps-cart",
      partialize: (state) => ({ items: state.items }),
      onRehydrateStorage: () => () => {
        useCartStore.setState({ hasHydrated: true });
      },
    }
  )
);

export function cartLines(items: Record<number, CartLine>): CartLine[] {
  return Object.values(items).sort((a, b) => a.name.localeCompare(b.name));
}

export function cartTotal(items: Record<number, CartLine>): number {
  return cartLines(items).reduce((sum, line) => sum + Number(line.unitPrice) * line.quantity, 0);
}

export function cartItemCount(items: Record<number, CartLine>): number {
  return cartLines(items).reduce((sum, line) => sum + line.quantity, 0);
}
